<script lang="ts">
  import { onDestroy, setContext } from "svelte";
  import { preventDefault } from "svelte/legacy";
  import { writable } from "svelte/store";
  import { focus } from "./actions/focus";
  import type { Bookmark } from "./bookmarks";
  import type { DirScan, DirStat } from "./dir-stats";
  import { scanDirStats } from "./dir-stats";
  import { filterByName } from "./file-filter";
  import type { FileListCmdApi, FileTableApi } from "./file-list-api";
  import { createListSelection } from "./file-list-select.svelte";
  import FileListCommands from "./FileListCommands.svelte";
  import FileListTopNav from "./FileListTopNav.svelte";
  import * as filepath from "./filepath";
  import FileTable from "./FileTable.svelte";
  import { FSDirState, fsFullDir } from "./fs";
  import type { FsDir, FsRow } from "./fsentry";
  import { kUpRow } from "./fsentry";
  import { captureEvent } from "./http";
  import Close from "./icons/Close.svelte";
  import File from "./icons/File.svelte";
  import Folder from "./icons/Folder.svelte";
  import type { MenuItem } from "./MenuItem";
  import type { PaneNav } from "./nav-history";
  import { paneNavKey } from "./nav-history";
  import { sortDown, sortFiles, sortNone, sortUp } from "./sort";
  import Spinner from "./Spinner.svelte";
  import { addBookmark, bookmarkForFS } from "./state.svelte";
  import { setShowUploadInfo, showUploadInfo } from "./store";
  import { fmtSize, len, truncatedText } from "./util";

  interface Props {
    fsState: FSDirState;
    currBookmark?: Bookmark;
    pickOtherFSState: any;
    gotobookmark: (b: Bookmark) => void;
    showtoplevel: () => void;
    // going back and forward in the places of the pane
    nav?: PaneNav;
    // called when a directory was read and is now shown
    ondirshown?: (dir: string) => void;
    errorMsg?: string;
  }

  let {
    fsState = $bindable(),
    currBookmark = $bindable(bookmarkForFS(fsState.fs, fsState.currDir)),
    pickOtherFSState,
    gotobookmark,
    showtoplevel,
    nav,
    ondirshown,
    errorMsg = $bindable<string>(""),
  }: Props = $props();

  fsState.onReloadCurrentDir = () => {
    reloadCurrentDir();
  };

  let fs = fsState.fs;
  let currDirFull = $state(fsState.currDir);

  let progressMsg = writable<string>();
  let entries: FsRow[] = $state([]);

  let nameSort = $state(sortNone);
  let sizeSort = $state(sortNone);
  let lastModSort = $state(sortNone);
  let typeSort = $state(sortNone);

  let nFiles = $state(0);
  let nDirs = $state(0);
  let totalSize = $state(0);
  let nSelectedFiles = $state(0);
  let nSelectedDirs = $state(0);
  let selectedSize = $state(0);

  let currentContextMenu = $state();
  let cmPageX = $state(0);
  let cmPageY = $state(0);
  // true if the menu was opened with the keyboard: its first item is selected
  let cmSelectFirst = $state(false);

  let elWrapper = $state<HTMLElement>();
  let isDragOver = $state(false);

  let cmds: FileListCmdApi = {
    openFile() {},
    onFileDrop: async (_ev: Event) => {},
    onContextMenu() {},
    showContextMenu() {},
    commands: () => [],
  };
  let tableApi: FileTableApi = {
    scrollToId() {},
  };

  // what is typed in the filter box under the top row: only the rows whose
  // name passes it are listed
  let filterText = $state("");
  let elFilter = $state<HTMLInputElement>();

  let filteredEntries: FsRow[] = $derived(filterByName(entries, filterText));

  let sortedEntries: FsRow[] = $derived.by(() => {
    console.log("calculating sortedEntries");
    if (filteredEntries.length == 0) {
      return [kUpRow];
    }
    let e = [kUpRow, ...filteredEntries];
    sortFiles(e, nameSort, sizeSort, lastModSort, typeSort);
    return e;
  });

  const sel = createListSelection({
    sorted: () => sortedEntries,
    wrapper: () => elWrapper,
    scrollToId: (id) => tableApi.scrollToId(id),
    openFile: (e) => cmds.openFile(e),
    dirUp: () => dirUp(),
    openDir: (name) => openDir(name),
  });

  // the rows change with the filter, so the selection starts over
  function onFilterInput() {
    sel.clearAndSelectFirst();
  }

  function clearFilter() {
    if (filterText != "") {
      filterText = "";
      sel.clearAndSelectFirst();
    }
  }

  // Keys typed in the filter box are text, not commands of the list. The
  // ones that move in the list or open the selected row still reach it, so
  // one can type, press Down and Enter without leaving the box.
  function onFilterKeyDown(ev: KeyboardEvent) {
    if (ev.key == "Escape") {
      ev.preventDefault();
      ev.stopPropagation();
      clearFilter();
      elWrapper?.focus();
      return;
    }
    if (ev.key == "ArrowUp" || ev.key == "ArrowDown" || ev.key == "Enter") {
      return;
    }
    ev.stopPropagation();
  }

  // "/" and Ctrl + F go to the filter box. Returns true if the key was one
  // of them. A "/" typed in the box itself does not get here.
  function filterKey(ev: KeyboardEvent): boolean {
    let ctrl = ev.ctrlKey || ev.metaKey;
    let isSlash = ev.key == "/" && !ctrl && !ev.altKey;
    let isCtrlF = ctrl && !ev.altKey && !ev.shiftKey && ev.key.toLowerCase() == "f";
    if (!isSlash && !isCtrlF) {
      return false;
    }
    ev.preventDefault();
    ev.stopPropagation();
    elFilter?.focus();
    elFilter?.select();
    return true;
  }

  // for the command palette
  export function getCommands(): MenuItem[] {
    return cmds.commands();
  }

  export function focusFilter() {
    elFilter?.focus();
    elFilter?.select();
  }

  function dismissContextMenu() {
    console.log("dismissContextMenu");
    // the menu had the keys: they go back to the list, unless something
    // else took the focus, like the other pane
    let hadFocus = !!document.activeElement?.closest(".context-menu");
    currentContextMenu = null;
    if (hadFocus) {
      elWrapper?.focus();
    }
  }

  // Right arrow shows the menu of the selected row, with its first item
  // selected. Returns true if the key was that.
  function contextMenuKey(ev: KeyboardEvent): boolean {
    if (ev.key != "ArrowRight" || ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) {
      return false;
    }
    ev.preventDefault();
    ev.stopPropagation();
    let e = sel.currentEntry();
    let el: Element | null | undefined = null;
    if (e) {
      tableApi.scrollToId(e.id);
      el = elWrapper?.querySelector('[data-id="' + CSS.escape(e.id) + '"]');
    }
    // under the row, a bit in from its left edge; without a row at the top
    // of the list
    let r = (el || elWrapper)?.getBoundingClientRect();
    if (!r) {
      return true;
    }
    cmSelectFirst = true;
    let x = r.left + Math.min(48, r.width / 2) + window.scrollX;
    let y = (el ? r.bottom : r.top + 48) + window.scrollY;
    cmds.showContextMenu(x, y, e);
    return true;
  }

  function onContextMenu(ev: MouseEvent) {
    cmSelectFirst = false;
    cmds.onContextMenu(ev);
  }

  setContext("fnDismissContextMenu", dismissContextMenu);

  function cycleSort(prevSort: number) {
    if (prevSort == sortNone && sizeSort != sortNone) {
      return sortUp;
    }
    if (prevSort == sortNone || prevSort == sortUp) {
      return sortDown;
    }
    return sortUp;
  }

  function cycleSortSize(prevSort: number) {
    if (prevSort == sortNone && nameSort != sortNone) {
      return sortUp;
    }
    if (prevSort == sortNone || prevSort == sortUp) {
      return sortDown;
    }
    return sortUp;
  }

  function changeNameSort() {
    let newSort = cycleSort(nameSort);
    console.log("changeNameSort:", newSort);
    nameSort = newSort;
    sizeSort = sortNone;
    lastModSort = sortNone;
    typeSort = sortNone;
    captureEvent("changesort", { type: "name" });
  }

  function changeSizeSort() {
    let newSort = cycleSortSize(sizeSort);
    console.log("changeSizeSort:", newSort);
    nameSort = sortNone;
    sizeSort = newSort;
    lastModSort = sortNone;
    typeSort = sortNone;
    captureEvent("changesort", { type: "size" });
  }

  function changeLastModSort() {
    let newSort = cycleSortSize(lastModSort);
    console.log("changeLastModSort:", newSort);
    nameSort = sortNone;
    sizeSort = sortNone;
    lastModSort = newSort;
    typeSort = sortNone;
    captureEvent("changesort", { type: "lastmod" });
  }

  // the first click sorts A to Z
  function changeTypeSort() {
    let newSort = typeSort == sortUp ? sortDown : sortUp;
    nameSort = sortNone;
    sizeSort = sortNone;
    lastModSort = sortNone;
    typeSort = newSort;
    captureEvent("changesort", { type: "type" });
  }

  function updateStatusString(selected: Record<string, FsRow>) {
    // counted apart and set once: this runs in an effect, which must not
    // read what it writes
    let files = 0;
    let dirs = 0;
    let size = 0;
    for (let e of Object.values(selected)) {
      if (e.name == "..") {
        // the way up is not one of the directories
        continue;
      }
      if (e.isDir) {
        dirs++;
      } else {
        files++;
        if (e.size) {
          size += e.size;
        }
      }
    }
    nSelectedFiles = files;
    nSelectedDirs = dirs;
    selectedSize = size;
  }

  // the counts and the size of the selection in the status row follow it
  $effect(() => {
    updateStatusString(sel.selected);
  });

  function updateFileStats(rows: FsRow[]) {
    console.log("updateFileStats");
    nFiles = 0;
    nDirs = 0;
    totalSize = 0;
    for (let e of rows) {
      if (e.isDir) {
        if (e.name != "..") {
          nDirs++;
        }
      } else {
        nFiles++;
        totalSize += e.size | 0;
      }
    }
    console.log("nFiles:", nFiles, "nDirs:", nDirs);
    updateStatusString(sel.selected);
  }

  function fsOnError(err: any) {
    progressMsg.set("");
    let s = err.toString();
    if (err.status) {
      s = `Failed with status ${err.status}`;
    }
    errorMsg = s;
    console.error(s);
  }

  function filterRows(rows: FsRow[]) {
    let res = [];
    for (let e of rows) {
      if (e.name == ".keep.filerion") {
        continue;
      }
      res.push(e);
    }
    return res;
  }

  // The sizes and dates of the directories listed, by name. They come from
  // reading everything under them, so they grow while that goes on.
  let dirStats: Record<string, DirStat> = $state({});
  let dirScan: DirScan | null = null;
  const scanProgress = writable("");

  // true if a pane shows the directory or is on its way to it: its listing
  // is in use
  function isDirShown(path: string): boolean {
    let states: (FSDirState | undefined)[] = [fsState];
    try {
      states.push(pickOtherFSState(fsState));
    } catch {
      // this pane is being replaced
    }
    for (let st of states) {
      if (st && st.fs == fs && (st.currDir == path || st.dirInProgress == path)) {
        return true;
      }
    }
    return false;
  }

  function startDirScan(dir: FsDir) {
    dirScan?.cancel();
    dirStats = {};
    let names: string[] = [];
    let n = dir.count();
    for (let i = 0; i < n; i++) {
      if (dir.entryIsDir(i)) {
        names.push(dir.entryName(i));
      }
    }
    if (len(names) == 0) {
      dirScan = null;
      return;
    }
    dirScan = scanDirStats({
      dirPath: dir.path,
      names: names,
      cache: fs.dirStats,
      readDir: async (path) => {
        // The listings read only for the scan are not kept: there can be
        // very many of them. The totals are, in fs.dirStats.
        let wasCached = fs.dirs.has(path);
        let d = await fs.readDir(path, false, scanProgress);
        if (!wasCached && !isDirShown(path)) {
          fs.removeCachedDir(path);
        }
        return d;
      },
      onUpdate: (name, stat) => {
        dirStats[name] = stat;
      },
    });
  }

  onDestroy(() => {
    dirScan?.cancel();
  });

  function onGotEntries(dir: FsDir) {
    console.log(`onGotEntries: ${dir.count()} entries`);
    entries = filterRows(dir.rows());
    startDirScan(dir);
    sel.clearAndSelectFirst();
    updateFileStats(sortedEntries);
  }

  function reloadCurrentDir() {
    console.log("reloadCurrentDir()");
    tryReadDir(fsState.currDir, true);
  }

  function dirUp() {
    console.log("dirUp");
    if (fsState.currDir == "" || fsState.currDir == "/") {
      showtoplevel();
      return;
    }
    console.log("dirUp 2");
    let { dir } = filepath.split(fsState.currDir);
    if (dir == "") {
      dir = "/";
    }
    tryReadDir(dir);
  }

  function openDir(name: string) {
    let newDir = fsState.currDir + "/" + name;
    newDir = newDir.replace("//", "/");
    tryReadDir(newDir);
  }

  export function tryReadDir(dirName: string, force = false) {
    console.log(`tryChangeDir, dir: ${dirName}, force: ${force}`);

    captureEvent("changedir", {
      dir: dirName,
    });

    fsState.dirInProgress = dirName;

    let savedIds: string[] = [];
    if (dirName == fsState.currDir) {
      savedIds.push(...sel.selectionIds());
    }

    function buildFullDir() {
      currDirFull = fsFullDir(fs, dirName);
    }

    let isCurrDirOk = errorMsg == "";

    if (force) {
      // read again because something changed in it: its size is not what
      // it was, nor is the size of the directories it is in
      fs.dirStats.invalidatePath(dirName);
    }
    fs.readDir(dirName, force, progressMsg).then(onDone).catch(onError);
    function onError(err: any) {
      fsState.isValidDir = false;
      err = unpackError(err);
      fsOnError(err);
      if (isCurrDirOk) {
        return;
      }
      console.log("onError but changing dir to:", dirName);
      fsState.currDir = dirName;
      buildFullDir();
    }
    function onDone(dir: FsDir) {
      progressMsg.set("");
      errorMsg = "";
      fsState.isValidDir = true;
      onGotEntries(dir);
      if (dirName != fsState.currDir) {
        // the filter was for the directory we left
        filterText = "";
      }
      fsState.currDir = dirName;
      currBookmark.dir = fsState.currDir;
      ondirshown?.(dirName);
      buildFullDir();
      sel.restoreIds(savedIds);
    }
  }

  tryReadDir(fsState.currDir, false);

  function unpackError(err: any) {
    if (err.error) {
      let sum = err.error.error_summary;
      if (sum.startsWith("path/not_found/")) {
        return `Directory '${fsState.dirInProgress}' doesn't exist`;
      }
      console.log(`unpackError: summary: ${sum}`);
    }
    return err.toString();
  }

  function showIsDragOver() {
    isDragOver = true;
  }
  function hideIsDragOver() {
    isDragOver = false;
  }
  function hideErrorMessage() {
    errorMsg = "";
  }

  function addToBookmarks() {
    addBookmark(bookmarkForFS(fsState.fs, fsState.currDir));
  }
</script>

<!-- ensure this class is generated by tailwind -->
<div class="bg-sky-200 hidden"></div>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="wrapper overflow-auto flex flex-col"
  class:showdragover={isDragOver}
  bind:this={elWrapper}
  use:focus
  tabindex="0"
  onkeydown={(ev) => paneNavKey(ev, nav) || filterKey(ev) || contextMenuKey(ev) || sel.handleKeyDown(ev)}
  ondragover={preventDefault(showIsDragOver)}
  ondragenter={preventDefault(showIsDragOver)}
  ondragleave={preventDefault(hideIsDragOver)}
  ondrop={preventDefault((ev) => cmds.onFileDrop(ev))}
>
  <FileListTopNav
    path={currDirFull}
    {currBookmark}
    addtobookmarks={addToBookmarks}
    fmdirup={dirUp}
    {nav}
    {gotobookmark}
  />
  <div class="grow-0 flex items-center px-2 py-1 bg-gray-200 text-xs">
    <input
      bind:this={elFilter}
      bind:value={filterText}
      oninput={onFilterInput}
      onkeydown={onFilterKeyDown}
      type="text"
      spellcheck="false"
      autocomplete="off"
      aria-label="Filter files"
      placeholder="Filter: words in the name, -word to leave out (/ or Ctrl + F)"
      class="grow px-2 py-0.5 bg-white border border-gray-300 outline-hidden focus:border-blue-600"
    />
    {#if filterText.trim() != ""}
      <div class="ml-2 text-gray-600 whitespace-nowrap">{len(filteredEntries)} of {len(entries)}</div>
    {/if}
  </div>

  <FileTable
    {sortedEntries}
    {dirStats}
    {nameSort}
    {sizeSort}
    {lastModSort}
    {typeSort}
    isSelected={sel.isSelected}
    multiSelect={len(Object.keys(sel.selected)) > 1}
    onRowClick={sel.markSelected}
    onRowMouseMove={sel.selectOnMouseMove}
    onNameSort={changeNameSort}
    onSizeSort={changeSizeSort}
    onLastModSort={changeLastModSort}
    onTypeSort={changeTypeSort}
    {onContextMenu}
    {currentContextMenu}
    {cmPageX}
    {cmPageY}
    {cmSelectFirst}
    {tableApi}
  />

  <div class="relative grow-0 bg-gray-50 pl-2">
    <div class="flex items-center">
      <Folder />
      <div class="ml-2">
        {nSelectedDirs} / {nDirs}
      </div>
      <div class="ml-2 mr-2 text-gray-400">•</div>
      <File />
      <div class="ml-2">{nSelectedFiles} / {nFiles}</div>
      <div class="ml-2 text-gray-400">•</div>
      <div class="ml-2">
        {fmtSize(selectedSize)} / {fmtSize(totalSize)}
      </div>
    </div>
    <div class="absolute flex flex-col bottom-8 left-2 right-2 bg-gray-50">
      {#if $showUploadInfo}
        <div
          class="flex flex-row justify-between items-center px-3 py-1 mb-2 text-blue-500 font-normal bg-blue-100 border border-blue-300 rounded-lg"
        >
          <div class="break-all">
            Drag & drop files from desktop to upload here. <a
              class="underline"
              target="_blank"
              rel="noreferrer"
              href="/doc/Working-with-files#uploading">Learn more</a
            >
          </div>
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div class="self-center mt-1 ml-2 hover:bg-blue-200 cursor-pointer" onclick={() => setShowUploadInfo(false)}>
            <Close />
          </div>
        </div>
      {/if}
      {#if errorMsg}
        <div
          class="flex flex-row justify-between items-center px-3 py-1 mb-2 text-red-500 font-semibold bg-red-100 border border-red-300 rounded-lg"
        >
          <div class="break-all">{truncatedText(errorMsg)}</div>
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div class="self-center mt-1 ml-2 hover:bg-red-200 cursor-pointer" onclick={hideErrorMessage}>
            <Close />
          </div>
        </div>
      {/if}

      {#if $progressMsg}
        <div
          class="flex flex-row items-center px-3 py-1 bg-yellow-100 text-gray-900 border border-yellow-300 rounded-lg"
        >
          <div class="mr-2 break-all">{$progressMsg}</div>
          <Spinner style="margin-top: 4px; color: black" />
        </div>
      {/if}
    </div>
  </div>
</div>

<FileListCommands
  {fs}
  {fsState}
  {progressMsg}
  {pickOtherFSState}
  bind:errorMsg
  getSelected={() => sel.selected}
  findEntryIdxById={sel.findEntryIdxById}
  isSelected={sel.isSelected}
  makeSingleSelected={sel.makeSingleSelected}
  getEntryFromContextMenuEvent={sel.getEntryFromContextMenuEvent}
  {reloadCurrentDir}
  {fsOnError}
  {cmds}
  bind:currentContextMenu
  bind:cmPageX
  bind:cmPageY
/>

<style lang="postcss">
  .wrapper {
    max-height: 100%;
    border: 1px solid white; /* don't re-layout when :focus-within */
    border: 2px solid #ccc;
  }

  .wrapper:focus-within {
    border: 2px solid gray;
    outline: none;
  }

  .showdragover {
    border: 2px solid blue !important;
  }
</style>
